import React, {useState} from "react";
import {
    Modal,
    Button,
    FormGroup,
    Checkbox,
    ControlLabel,
    Glyphicon,
    Collapse
} from "react-bootstrap";
import {
    filterOptions,
    cloneEventParameters,
} from "../utils/Common";
import "./FilterModal.css";
import CamViews from "../images/cam-view-numbers.png";
import CamViews2 from "../images/cam-view-numbers-2cam.png";

function SectionHeader({titleText, helpText, onHelpClick}) {
    return (
        <div className="filter-modal-section-header">
            <ControlLabel>{titleText}</ControlLabel>
            <Button
                className="filter-help-btn"
                bsSize="small"
                aria-label={`Help for ${titleText}`}
                onClick={() => onHelpClick(helpText, titleText === "Camera Views to Include")}
            >
                <Glyphicon glyph="info-sign" />
            </Button>
        </div>
    );
}

function CameraCheckbox({idx, checked, onChange}) {
    return (
        <Checkbox checked={checked} onChange={onChange}>
            Camera #{idx + 1}
        </Checkbox>
    );
}

export default function MotionHistoryFilterModal({
    show,
    setShowFilterModal,
    eventParamaters,
    setEventParameters,
    applyFilters,
    architecture,
}) {
    const [showHelp, setShowHelp] = useState(false);
    const [helpText, setHelpText] = useState("");
    const [showCamHelp, setShowCamHelp] = useState(false);

    function handleSelectionChange(idx) {
        const tempEventParams = cloneEventParameters(eventParamaters);
        tempEventParams.selectedFilters[filterOptions[idx]] = !tempEventParams.selectedFilters[filterOptions[idx]];
        setEventParameters(tempEventParams);
    }

    function handleBooleanParamChange(property) {
        const tempEventParams = cloneEventParameters(eventParamaters);
        tempEventParams[property] = !tempEventParams[property];
        setEventParameters(tempEventParams);
    }

    function resetFilters() {
        const tempEventParams = cloneEventParameters(eventParamaters);
        for(let i = 0; i < 3; i++) {
            tempEventParams.selectedFilters[filterOptions[i]] = true;
        }
        tempEventParams.cars = true;
        tempEventParams.persons = true;
        tempEventParams.realMotion = true;
        tempEventParams.sortParameterNumber = 0;
        setEventParameters(tempEventParams);
    }

    function openHelp(text, isCameraHelp) {
        setShowHelp(true);
        setHelpText(text);
        setShowCamHelp(isCameraHelp);
    }

    return (
        <Modal
            show={show}
            onHide={() => setShowFilterModal(false)}
            className="filter-modal"
            aria-labelledby="motion-filter-modal-title"
            dialogClassName="modal-dialog"
            backdrop={true}
        >
            <Modal.Header closeButton>
                <div className="filter-modal-title" id="motion-filter-modal-title">Motion Filters</div>
            </Modal.Header>
            <Modal.Body>
                <div className="filter-modal-grid">
                    <div className="filter-modal-column">
                        <FormGroup>
                            <SectionHeader
                                titleText="Camera Views to Include"
                                helpText=""
                                onHelpClick={openHelp}
                            />
                            <div className="filter-modal-options">
                                <CameraCheckbox
                                    idx={0}
                                    checked={eventParamaters.selectedFilters[filterOptions[0]]}
                                    onChange={() => handleSelectionChange(0)}
                                />
                                <CameraCheckbox
                                    idx={1}
                                    checked={eventParamaters.selectedFilters[filterOptions[1]]}
                                    onChange={() => handleSelectionChange(1)}
                                />
                                {!architecture && (
                                    <CameraCheckbox
                                        idx={2}
                                        checked={eventParamaters.selectedFilters[filterOptions[2]]}
                                        onChange={() => handleSelectionChange(2)}
                                    />
                                )}
                                {!architecture && (
                                    <CameraCheckbox
                                        idx={3}
                                        checked={eventParamaters.selectedFilters[filterOptions[3]]}
                                        onChange={() => handleSelectionChange(3)}
                                    />
                                )}
                            </div>
                        </FormGroup>
                    </div>
                    <div className="filter-modal-column">
                        <FormGroup>
                            <SectionHeader
                                titleText="Types of Detections"
                                helpText={"Our cameras detect lots of things that move on site, cars, trucks, people, etc. For simplicity, we've grouped these into two categories, People and Vehicles. If you're looking for a specific event, you may find filtering out one of these groups to be helpful in your search."}
                                onHelpClick={openHelp}
                            />
                            <div className="filter-modal-options">
                                <Checkbox
                                    checked={eventParamaters.persons}
                                    onChange={() => handleBooleanParamChange("persons")}
                                >
                                    People
                                </Checkbox>
                                <Checkbox
                                    checked={eventParamaters.cars}
                                    onChange={() => handleBooleanParamChange("cars")}
                                >
                                    Vehicles
                                </Checkbox>
                            </div>
                        </FormGroup>
                        <FormGroup>
                            <SectionHeader
                                titleText="Include All Detections"
                                helpText={"Your camera system detects a lot of events. In general, it errs on the side of caution, and uploads events that may not be real motion. Our servers take a second look at every event that hits our system and verifies those that it deems more likely to be 'real.' It marks those and we show you those by default. If you want to see all of the events uploaded to our system, you can check this box."}
                                onHelpClick={openHelp}
                            />
                            <div className="filter-modal-options">
                                <Checkbox
                                    checked={!eventParamaters.realMotion}
                                    onChange={() => handleBooleanParamChange("realMotion")}
                                >
                                    Unverified Motion
                                </Checkbox>
                            </div>
                        </FormGroup>
                    </div>
                </div>

                <Collapse in={showHelp}>
                    <div className="filter-modal-help">
                        {helpText}
                        {showCamHelp && (
                            <div className="filter-modal-help-image">
                                <img
                                    src={architecture ? CamViews2 : CamViews}
                                    alt="Top Left: Camera #1 | Top Right: Camera #2 | Bottom Left: Camera #3 | Bottom Right: Camera #4"
                                />
                            </div>
                        )}
                    </div>
                </Collapse>

                <div className="filter-modal-actions">
                    <Button
                        className="btn-brand-dark"
                        block
                        onClick={() => {
                            applyFilters();
                            setShowFilterModal(false);
                        }}
                    >
                        Apply Filters
                    </Button>
                    <Button
                        className="btn-brand-outline"
                        block
                        onClick={resetFilters}
                    >
                        Reset Filters
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}
